import { keepUndeleted } from 'shared/domain/deletable/filters';
import { LevelOnView } from 'shared/domain/level/types/view';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { ensureArray } from 'shared/utils/array';

export interface GroupedLabelledEntity extends LabelledEntity {
  readonly group: string;
}

/**
 * Filters levels for a single selected site.
 * Used by forms with single-site selection (e.g. issue form).
 * Accepts single entity or array (uses first element for backward compatibility).
 */
export function getLevelsForSingleSite(
  levels: HashMap<LevelOnView[]>,
  site: readonly LabelledEntity[] | LabelledEntity | undefined
): LabelledEntity[] {
  const normalizedSite = Array.isArray(site) ? site[0] : site;
  if (!normalizedSite) return [];
  const levelsOnSite = levels[normalizedSite._id];
  return Array.isArray(levelsOnSite)
    ? levelsOnSite.filter(keepUndeleted)
    : [];
}

/**
 * Filters and groups levels by multiple selected sites.
 * Pure function - no side effects, easily testable.
 * Handles undefined, single site (legacy), or array of sites.
 * 
 * Note: Signature matches extractor convention used by statefulInputsWrapper:
 * (availableItems: HashMap, dependencyValue: T | T[])
 */
export function getLevelsForMultipleSites(
  levelsPerSite: HashMap<LevelOnView[]>,
  sites: readonly LabelledEntity[] | LabelledEntity | undefined
): GroupedLabelledEntity[] {
  const normalizedSites = ensureArray(sites);
  if (normalizedSites.length === 0) return [];

  return normalizedSites.flatMap((site) => {
    const siteLevels = levelsPerSite[site._id] ?? [];
    const filteredLevels = siteLevels.filter(keepUndeleted);
    return filteredLevels.map((level) => ({
      _id: level._id,
      label: level.label,
      group: site.label,
    }));
  });
}

/**
 * Checks if any of the selected sites have protocol issues.
 * Used to determine if site selection should be disabled.
 * Handles undefined, single ID, or array of IDs.
 */
export function hasAnyProtocolIssuesForSites(
  siteIds: readonly string[] | string | undefined,
  protocolIssuesBySite: HashMap<boolean>
): boolean {
  const normalizedIds = ensureArray(siteIds);
  return normalizedIds.some((siteId) => protocolIssuesBySite[siteId] === true);
}
